/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import {
  FilledButton,
  OutlinedButton,
  SideNavigation,
  Spotlight,
  SpotlightTarget,
  useNeedleTheme,
  useSpotlightContext,
} from '@neo4j-ndl/react';
import { useEffect, useState } from 'react';

const Component = (props: { isChromatic: boolean }) => {
  const { themeClassName } = useNeedleTheme();
  const { setActiveSpotlight, setIsOpen } = useSpotlightContext();

  const [isSelected, setIsSelected] = useState<string | null>(
    'aura-db-instances',
  );
  const targetId = `test-target-${themeClassName}`;

  const handleClick = (item: string) => {
    setIsSelected(item);
  };

  useEffect(() => {
    if (props.isChromatic) {
      setActiveSpotlight(targetId);
      setIsOpen(true);
    }
  }, [setActiveSpotlight, setIsOpen, targetId, props.isChromatic]);

  return (
    <div className="n-h-[500px] n-flex">
      <SideNavigation isExpanded={true} ariaLabel="Main side navigation">
        <SideNavigation.CategoryHeader>AuraDB</SideNavigation.CategoryHeader>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            onClick={() => handleClick('aura-db-instances')}
            isActive={isSelected === 'aura-db-instances'}
            label="Instances"
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            onClick={() => handleClick('aura-db-connect')}
            isActive={isSelected === 'aura-db-connect'}
            label="Connect"
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            onClick={() => handleClick('aura-db-python')}
            isActive={isSelected === 'aura-db-python'}
            label="Python"
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SpotlightTarget
            id={targetId}
            hasPulse={!props.isChromatic}
            borderRadius={4}
            indicatorVariant="point"
            indicatorPlacement="top-right"
            asChild
          >
            <SideNavigation.NavItem
              onClick={() => handleClick('aura-db-javascript')}
              isActive={isSelected === 'aura-db-javascript'}
              label="JavaScript"
            />
          </SpotlightTarget>
        </SideNavigation.ListItem>
        <SideNavigation.CategoryHeader>AuraDS</SideNavigation.CategoryHeader>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            onClick={() => handleClick('aura-ds-instances')}
            isActive={isSelected === 'aura-ds-instances'}
            label="Instances"
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            onClick={() => handleClick('aura-ds-getting-started')}
            isActive={isSelected === 'aura-ds-getting-started'}
            label="Getting Started"
          />
        </SideNavigation.ListItem>
      </SideNavigation>
      <div className="n-w-full n-h-full n-flex n-flex-col n-items-center n-justify-center n-rounded-md n-p-token-16">
        <FilledButton onClick={() => setActiveSpotlight(targetId)}>
          Activate Spotlight
        </FilledButton>
      </div>
      <Spotlight
        placement="top-end-top-start"
        target={targetId}
        actions={
          <OutlinedButton
            onClick={() => {
              setIsOpen(false);
              setActiveSpotlight(null);
            }}
          >
            Close
          </OutlinedButton>
        }
      >
        <Spotlight.Header>This is a header</Spotlight.Header>
        <Spotlight.Body>This is content</Spotlight.Body>
      </Spotlight>
    </div>
  );
};

export default Component;
